/**
 * CSV Validation Utilities
 * 
 * Validate CSV data for outbound campaigns
 */

/**
 * Validate phone number format
 */
export const isValidPhoneNumber = (phone) => {
  if (!phone) return false;
  
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');
  
  // Check if it's 10 or 11 digits (US format)
  return digits.length === 10 || digits.length === 11;
};

/**
 * Validate date format and ensure it's in the future
 */
export const isValidDate = (dateString) => {
  if (!dateString) return { valid: false, message: 'Date is required' };
  
  // Try to parse the date
  const date = new Date(dateString);
  
  // Check if valid date
  if (isNaN(date.getTime())) {
    return { valid: false, message: 'Invalid date format' };
  }
  
  // Check if date is in the future
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  if (date < today) {
    return { valid: false, message: 'Appointment date is in the past' };
  }
  
  // Check if date is within reasonable range (next 12 months)
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 1);
  
  if (date > maxDate) {
    return { valid: false, message: 'Appointment date is too far in the future (max 12 months)' };
  }
  
  return { valid: true };
};

/**
 * Validate time format
 */
export const isValidTime = (timeString) => {
  if (!timeString) return { valid: false, message: 'Time is required' };
  
  // Match formats: "10:30 AM", "2:00 PM", "14:30", etc.
  const timeRegex = /^(0?[1-9]|1[0-2]):([0-5][0-9])\s?(AM|PM|am|pm)$|^([01]?[0-9]|2[0-3]):([0-5][0-9])$/;
  
  if (!timeRegex.test(timeString.trim())) {
    return { valid: false, message: 'Invalid time format (use HH:MM AM/PM or HH:MM 24-hour)' };
  }
  
  return { valid: true };
};

/**
 * Validate that field is not empty and meets min length
 */
export const isValidField = (value, minLength = 2) => {
  if (!value || typeof value !== 'string') {
    return { valid: false, message: 'Field is required' };
  }
  
  const trimmed = value.trim();
  
  if (trimmed.length < minLength) {
    return { valid: false, message: `Field must be at least ${minLength} characters` };
  }
  
  return { valid: true };
};

/**
 * Validate entire CSV dataset
 */
export const validateCsvData = (data) => {
  const errors = [];
  const phoneNumbers = new Set();
  
  // Required column names (case-insensitive)
  const requiredColumns = [
    'phone number',
    'first name',
    'last name',
    'dob',
    'appointment date',
    'appointment time',
    'doctor'
  ];
  
  // Check if data is empty
  if (!data || data.length === 0) {
    errors.push({ message: 'CSV file is empty' });
    return { isValid: false, errors };
  }
  
  // Check if required columns exist (check first row keys)
  const firstRow = data[0];
  const columnNames = Object.keys(firstRow).map(k => k.toLowerCase().trim());
  
  const missingColumns = requiredColumns.filter(
    col => !columnNames.includes(col)
  );
  
  if (missingColumns.length > 0) {
    errors.push({ 
      message: `Missing required columns: ${missingColumns.join(', ')}` 
    });
    return { isValid: false, errors };
  }
  
  // Validate each row
  data.forEach((row, index) => {
    const rowNum = row.rowNumber || index + 2; // +2 because row 1 is header
    
    // Normalize column names (handle different casings)
    const normalizedRow = {};
    Object.keys(row).forEach(key => {
      normalizedRow[key.toLowerCase().trim()] = row[key];
    });
    
    // Check if row is completely empty (skip validation if so)
    const isEmptyRow = Object.values(normalizedRow).every(value => 
      !value || (typeof value === 'string' && value.trim() === '')
    );
    
    if (isEmptyRow) {
      // Skip empty rows entirely
      return;
    }
    
    // First Name validation
    const firstNameCheck = isValidField(normalizedRow['first name'], 2);
    if (!firstNameCheck.valid) {
      errors.push({ 
        message: `Row ${rowNum}: Missing or invalid first name` 
      });
    }
    
    // Last Name validation
    const lastNameCheck = isValidField(normalizedRow['last name'], 2);
    if (!lastNameCheck.valid) {
      errors.push({ 
        message: `Row ${rowNum}: Missing or invalid last name` 
      });
    }
    
    // DOB validation
    const dobCheck = isValidField(normalizedRow['dob'], 1);
    if (!dobCheck.valid) {
      errors.push({ 
        message: `Row ${rowNum}: Missing or invalid date of birth` 
      });
    }
    
    // Phone Number validation
    if (!isValidPhoneNumber(normalizedRow['phone number'])) {
      errors.push({ 
        message: `Row ${rowNum}: Invalid phone number format (${normalizedRow['phone number'] || 'empty'})` 
      });
    } else {
      // Check for duplicates
      const cleanPhone = normalizedRow['phone number'].replace(/\D/g, '');
      if (phoneNumbers.has(cleanPhone)) {
        errors.push({ 
          message: `Row ${rowNum}: Duplicate phone number (${normalizedRow['phone number']})` 
        });
      }
      phoneNumbers.add(cleanPhone);
    }
    
    // Appointment Date validation
    const dateCheck = isValidDate(normalizedRow['appointment date']);
    if (!dateCheck.valid) {
      errors.push({ 
        message: `Row ${rowNum}: ${dateCheck.message} (${normalizedRow['appointment date'] || 'empty'})` 
      });
    }
    
    // Appointment Time validation
    const timeCheck = isValidTime(normalizedRow['appointment time']);
    if (!timeCheck.valid) {
      errors.push({ 
        message: `Row ${rowNum}: ${timeCheck.message} (${normalizedRow['appointment time'] || 'empty'})` 
      });
    }
    
    // Doctor validation
    const doctorCheck = isValidField(normalizedRow['doctor'], 2);
    if (!doctorCheck.valid) {
      errors.push({ 
        message: `Row ${rowNum}: Missing or invalid doctor name` 
      });
    }
  });
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

